import { Grid, useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { ForeignResidenceCrumbs } from 'subscription/constants/breadcrumb';
import { initialValues } from 'subscription/constants/subscription-birth';
import { toast } from 'react-toastify';
import { CommonInterfaceSubscription } from 'subscription/view-models/common-interface-subscription';
import { ResponseAnprSaveData } from 'shell/models/person';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { __PERSON_API_URL__, __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import validationSchemaForeignResidence from './validation-schema';
import PmiTitle from 'shell/components/shared/title';
import { IDFlags } from 'shell/components/shared/flag-validation-fiscal-code/flag';
import ForeignResidenceButtons from 'subscription/components/foreign-residence/buttons';
import SubscriptionForeignResidence from 'subscription/components/foreign-residence';
import ModalChangeParenthood, {
  FormModal,
} from 'practice/components/edit/form/shared/modal-change-parenthood';
import { OtherFamiliarsServiceResponse } from 'shell/models/other-familiars';
import { SubjectData } from 'shell/models/person';
import AireSubscriptionProvider from 'subscription/context/aire-subscription/provider';
import {
  useAireSubscription,
  SubscriptionForeignResidenceStep,
} from 'subscription/context/aire-subscription';

const ForeignResidencePageContent = (): React.ReactElement => {
  const navigate = useNavigate();

  const {
    currentStep,
    setCurrentStep,
    intestatarioData,
    setIntestatarioData,
    praticaKey,
    setPraticaKey,
    clearState,
  } = useAireSubscription();

  const [modalFamiliarsData, setModalFamiliarsData] = useState<FormModal>({
    otherFamiliars: [],
    newFamiliars: [],
  });

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const [openModalParenthood, setOpenModalParenthood] = useState(false);

  const avvioIscrizione = useHttpClient<HttpResponse<unknown>, unknown>(
    `${__PERSON_API_URL__}/persona/iscrizione-aire-anpr/avvio-iscrizione-accertata-residenza`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
      },
    }
  );

  const [responseConferma, confermaIscrizione] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/iscrizione-aire-anpr/iscrizione-accertata-residenza`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseConferma?.data as SaveDataAnpr;

  const [, getPersonaParentela] = useHttpFetch<
    HttpResponse<OtherFamiliarsServiceResponse>,
    FormModal
  >(
    praticaKey
      ? `${__PRACTICE_API_URL__}/persona-parentela/list/${praticaKey.cdEnte}-${praticaKey.idAnnoPratica}-IAIRE-${praticaKey.pgPratica}`
      : '',
    {
      onBootstrap: false,
      transformer: (response) => {
        const payload = response?.data?.payload;
        const oldFamiliars = payload?.altriFamiliari || [];
        const newFamiliars = payload?.nuoviFamiliari || [];

        if (oldFamiliars.length === 0 && newFamiliars.length === 0) {
          return {
            otherFamiliars: [],
            newFamiliars: [],
          };
        }

        if (newFamiliars.length > 0) {
          const casted = intestatarioData as SubjectData;
          const codiceFiscale =
            typeof casted?.generalita?.codiceFiscale === 'string'
              ? casted.generalita.codiceFiscale
              : casted?.generalita?.codiceFiscale?.codFiscale || '';

          const intestatarioVM = {
            fiscalCode: codiceFiscale,
            name: casted?.generalita?.nome || '',
            surname: casted?.generalita?.cognome || '',
            parenthoodValue: '1',
            elementFamilyPractice: {
              codFiscale: codiceFiscale,
              nome: casted?.generalita?.nome || '',
              cognome: casted?.generalita?.cognome || '',
              sesso: casted?.generalita?.sesso || '',
              dataNascita: casted?.generalita?.dataNascita || '',
              codAnprParentela: '1',
              key: praticaKey
                ? {
                    tipoRegistro: 'IAIRE',
                    codiceEnte: praticaKey.cdEnte,
                    idDbLocalePersona: 0,
                    idAnnoPratica: praticaKey.idAnnoPratica,
                    progressivoPratica: praticaKey.pgPratica,
                    idAnprPersona: '0',
                  }
                : undefined,
              flagIntestatario: '1',
              statusPersona: '8',
              flagAire: '0',
              posizionePersona: 'I',
              codCittadinanza: (casted?.cittadinanza?.[0]?.codiceStato || 100).toString(),
              codComuneNascita: casted?.idComuneNascita,
              codNazioneEsteraNascita: casted?.idStatoNascita || 100,
              descrizioneComuneNascita: undefined,
              codComunePrecedente: undefined,
              controlloCf: '1',
            },
            isMinorenne: false,
          };

          const mappedNewFamiliars = newFamiliars.map((element) => ({
            fiscalCode: element.codFiscale || '',
            name: element.nome || '',
            surname: element.cognome || '',
            parenthoodValue: element.codAnprParentela?.toString() || '',
            elementFamilyPractice: element,
            isMinorenne: false,
          }));

          return {
            otherFamiliars: [],
            newFamiliars: [intestatarioVM, ...mappedNewFamiliars],
          };
        }

        return {
          otherFamiliars: oldFamiliars.map((element) => ({
            fiscalCode: element.codFiscale || '',
            name: element.nome || '',
            surname: element.cognome || '',
            parenthoodValue: element.codAnprParentela?.toString() || '',
            element,
            isMinorenne: false,
          })),
          newFamiliars: [],
        };
      },
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
      },
    }
  );

  const handleAvanzaStepAnagrafe = useEventCallback((intestatario: unknown) => {
    const castedIntestatario = intestatario as Record<string, unknown>;

    const intestatarioWithLegame = {
      ...castedIntestatario,
      legameSoggetto: castedIntestatario?.legameSoggetto || {
        codiceLegame: '1',
        tipoLegame: '5',
      },
    };

    (
      avvioIscrizione({
        data: { intestatario: intestatarioWithLegame },
      }) as ResponsePromise<HttpResponse<unknown>, unknown>
    )
      .then((response) => {
        const axiosResponse = response as { data: HttpResponse<Record<string, unknown>> };
        const praticaKeyData = axiosResponse?.data?.payload;

        if (praticaKeyData?.cdEnte && praticaKeyData?.pgPratica && praticaKeyData?.idAnnoPratica) {
          setPraticaKey({
            cdEnte: praticaKeyData.cdEnte as string,
            tipoPratica: (praticaKeyData.tipoPratica as string) || 'C04',
            idAnnoPratica: praticaKeyData.idAnnoPratica as number,
            pgPratica: praticaKeyData.pgPratica as number,
          });

          setIntestatarioData(intestatario);

          setCurrentStep(SubscriptionForeignResidenceStep.Family);
          toast.success('Pratica creata con successo');
        } else {
          toast.error('Errore nella creazione della pratica');
        }
      })
      .catch(() => {
        toast.error("Errore durante l'avvio dell'iscrizione");
      });
  });

  const handleAvanzaFamiglia = useEventCallback(() => {
    setCurrentStep(SubscriptionForeignResidenceStep.Reesidence);
    toast.success('Passaggio allo step Residenza');
  });

  const handleIndietroFamiglia = useEventCallback(() => {
    setCurrentStep(SubscriptionForeignResidenceStep.RegistryOffice);
  });

  const handleAvanzaResidenza = useEventCallback(() => {
    setCurrentStep(SubscriptionForeignResidenceStep.Attachments);
    toast.success('Passaggio allo step Allegati');
  });

  const handleIndietroResidenza = useEventCallback(() => {
    setCurrentStep(SubscriptionForeignResidenceStep.Family);
  });

  const handleIndietroAllegati = useEventCallback(() => {
    setCurrentStep(SubscriptionForeignResidenceStep.Reesidence);
  });

  const onCloseModalParenthood = useEventCallback((isConfirm: boolean) => {
    setOpenModalParenthood(false);

    if (isConfirm) {
      handleSubmit();
    }
  });

  const handleAvanzaAllegati = useEventCallback(() => {
    if (!praticaKey) {
      toast.error('Chiave pratica mancante. Completare prima lo step Anagrafe.');
      return;
    }

    (
      getPersonaParentela() as ResponsePromise<
        HttpResponse<OtherFamiliarsServiceResponse>,
        FormModal
      >
    )
      .then((response) => {
        let transformedData: FormModal;

        if ('data' in response) {
          transformedData = response.data as unknown as FormModal;
        } else {
          // Già trasformato - la response è direttamente FormModal
          transformedData = response as FormModal;
        }

        const oldFamiliars = transformedData?.otherFamiliars || [];
        const newFamiliars = transformedData?.newFamiliars || [];

        if (oldFamiliars.length > 0 || newFamiliars.length > 0) {
          setModalFamiliarsData(transformedData);
          setOpenModalParenthood(true);
          return;
        }

        handleSubmit();
      })
      .catch(() => {
        toast.error('Errore nel recupero dei familiari');
      });
  });

  const handleSubmit = useEventCallback(() => {
    if (!praticaKey) {
      toast.error('Chiave pratica mancante. Completare prima lo step Anagrafe.');
      return;
    }

    (
      confermaIscrizione({
        data: {
          codiceEnte: praticaKey.cdEnte,
          tipoRegistro: 'IAIRE',
          idAnnoPratica: praticaKey.idAnnoPratica,
          progressivoPratica: praticaKey.pgPratica,
        },
      }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
    )
      .then((response) => {
        const castedResponse = response as SaveDataAnpr;
        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        toast.success('Iscrizione AIRE completata con successo');
        navigate('/iscrizioni');
      })
      .catch(() => {
        toast.error("Errore durante la conferma dell'iscrizione");
      });
  });

  const cancellOperation = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/annullo-operazione`, {
    options: { method: 'POST' },
  });

  const handleExit = useCallback(() => {
    if (!praticaKey) {
      clearState();
      navigate('/iscrizioni');
      return;
    }

    (
      cancellOperation({
        data: {
          codiceEnte: praticaKey.cdEnte,
          tipoRegistro: 'IAIRE',
          idAnnoPratica: praticaKey.idAnnoPratica,
          progressivoPratica: praticaKey.pgPratica,
        },
      }) as ResponsePromise<void>
    )
      .then(() => {
        clearState();
        toast.success('Operazione annullata con successo');
        navigate('/iscrizioni');
      })
      .catch(() => {
        clearState();
        toast.error("Errore durante l'annullamento della pratica");
        navigate('/iscrizioni');
      });
  }, [navigate, praticaKey, cancellOperation, clearState]);

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  const onPressStep = useCallback(
    (step: number) => {
      setCurrentStep(step);
    },
    [setCurrentStep]
  );

  return (
    <Fragment>
      <Breadcrumbs items={ForeignResidenceCrumbs} />
      <div>
        <PmiTitle title="Iscrizione AIRE per residenza all'estero" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      {openModalParenthood && (
        <ModalChangeParenthood
          open={openModalParenthood}
          onClose={onCloseModalParenthood}
          codeEnte={praticaKey?.cdEnte}
          typeRegister="IAIRE"
          yearId={praticaKey?.idAnnoPratica?.toString()}
          progressive={praticaKey?.pgPratica?.toString()}
          otherFamiliars={modalFamiliarsData.otherFamiliars}
          newFamiliars={modalFamiliarsData.newFamiliars?.filter(
            (member) =>
              member.elementFamilyPractice?.key?.idDbLocalePersona &&
              member.elementFamilyPractice.key.idDbLocalePersona !== 0
          )}
        />
      )}
      <Formik
        initialValues={
          {
            ...initialValues,
            addressForeign: {
              address: '',
            },
            citizenship: {
              ...initialValues.citizenship,
              state: { id: '100', value: 'ITALIA' },
            },
            familyCoexistence: undefined,
            editDataControl: {
              ...initialValues.editDataControl,
              flag: IDFlags.Attribuzione,
              subscribeTo: '5', // "Altra famiglia" fisso
              dateValidity: null,
            },
            generality: {
              ...initialValues.generality,
              isForeign: true,
              aire: 'S',
            },
            birthdayDocument: {
              transcript: true, // fisso
              municipality: null,
              year: '',
              documentNumber: '',
              portion: '',
              serie: '',
              volume: '',
            },
          } as CommonInterfaceSubscription
        }
        onSubmit={handleSubmit}
        validationSchema={validationSchemaForeignResidence}
      >
        <Form>
          <Grid container spacing={2}>
            <ForeignResidenceButtons
              handleExit={handleExit}
              currentStep={currentStep}
              onPressStep={onPressStep}
            />
            <Grid item xs={12} md={10}>
              <SubscriptionForeignResidence
                onAvanzaAnagrafe={handleAvanzaStepAnagrafe}
                onAvanzaFamiglia={handleAvanzaFamiglia}
                onIndietroFamiglia={handleIndietroFamiglia}
                onAvanzaResidenza={handleAvanzaResidenza}
                onIndietroResidenza={handleIndietroResidenza}
                onAvanzaAllegati={handleAvanzaAllegati}
                onIndietroAllegati={handleIndietroAllegati}
              />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Fragment>
  );
};

ForeignResidencePageContent.displayName = 'Subscription Foreign residence page component content';

const ForeignResidencePage = (): React.ReactElement => {
  return (
    <AireSubscriptionProvider>
      <ForeignResidencePageContent />
    </AireSubscriptionProvider>
  );
};

ForeignResidencePage.displayName = 'Foreign Residence Page';

export default ForeignResidencePage;
